// ── ProductInfoSection.jsx ────────────────────────────────────────────────────
// ✅ FIX 1: sellerIdentifier now matches useFollowSeller — falls back to
//           product.seller (username/email) so follow status loads even when
//           seller_email is not in the serializer response.
// ✅ FIX 2: Avatar resolver priority corrected — logged-in user vs other
//           seller are now properly distinguished (was always showing
//           authProfileImage regardless of whose listing it was).
// ✅ FIX 3: Removed own local follow state/API calls — follow is now driven
//           entirely by useProductModal (which uses useFollowSeller), so the
//           inline Follow button receives isFollowing / followCount /
//           isFollowLoading / onFollowToggle as props and stays in sync with
//           SellerSidebar. Duplicated local state caused them to diverge.

import React, { useState, useEffect, useRef } from 'react';
import styles from './ProductInfoSection.module.css';
import apiService from '../../../../services/api';
import { useAuthContext } from '../../../../context/AuthContext';
// import PhysicalBusinessAddress from './PhysicalBusinessAddress';

const CONDITION_CONFIG = {
  new:           { emoji: '✅', label: 'New' },
  slightly_used: { emoji: '🔎', label: 'Slightly Used' },
  used:          { emoji: '🔧', label: 'Used' },
};

// ── Helper: resolve relative image paths to absolute URLs ────────────────────
const resolveUrl = (url) => {
  if (!url || typeof url !== 'string') return null;
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) return url;
  const host = 'https://imselling-marketplace-production.up.railway.app';
  return `${host}${url.startsWith('/') ? '' : '/'}${url}`;
};

const ProductInfoSection = ({
  product,
  showSellerInfo,
  showCopyNotification,
  checkAuthentication,
  onContactSeller,
  onMakeOffer,
  onMessageSeller,
  onOpenRating,
  onSaveToWishlist,
  onCopyLink,
  // ✅ FIX 3: Follow props from useProductModal (via useFollowSeller)
  isFollowing,
  followCount,
  isFollowLoading,
  onFollowToggle,
}) => {
  // ── Auth context — same source the Header uses for the profile image ───────
  const { profileImage: authProfileImage } = useAuthContext();

  // ── Description scroll state ──────────────────────────────────────────────
  const [descExpanded, setDescExpanded] = useState(false);
  const [descOverflows, setDescOverflows] = useState(false);
  const descRef = useRef(null);

  const DESC_MAX_HEIGHT = 140;

  useEffect(() => {
    if (descRef.current) {
      setDescOverflows(descRef.current.scrollHeight > DESC_MAX_HEIGHT + 4);
    }
  }, [product?.description]);

  // ── Seller profile image ──────────────────────────────────────────────────
  const [sellerProfileImageUrl, setSellerProfileImageUrl] = useState(null);

  // ── Seller full profile (for PhysicalBusinessAddress when re-enabled) ─────
  const [sellerProfile, setSellerProfile] = useState(null);

  // ✅ FIX 1: Identifier chain matches useFollowSeller exactly —
  //   product.email → product.seller_email → product.seller (username = email
  //   in OTP auth) so follow status and avatar fetch use the same key.
  const sellerIdentifier =
    product?.email        ||
    product?.seller_email ||
    product?.seller       ||
    product?.sellerName   ||
    null;

  // ✅ FIX 2: Corrected avatar resolution priority.
  //   Old code always applied authProfileImage first, so viewing someone
  //   else's listing showed the logged-in user's own photo.
  //   New logic: only use authProfileImage when the viewer IS the seller.
  useEffect(() => {
    const resolve = (url) => resolveUrl(url);

    // ── Helper: probe whether a URL actually loads as an image ────────────
    const probeImage = (url) =>
      new Promise((res) => {
        const img = new Image();
        img.onload  = () => res(true);
        img.onerror = () => res(false);
        img.src = url;
      });

    const resolveSellerImage = async () => {
      // Step 1: Determine if the logged-in user IS the seller
      const loggedInUser = (() => {
        try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
      })();
      const loggedInEmail = (loggedInUser?.email || loggedInUser?.identifier || '').toLowerCase().trim();
      const sellerEmail   = (product?.email || product?.seller_email || '').toLowerCase().trim();

      if (loggedInEmail && sellerEmail && loggedInEmail === sellerEmail) {
        // Viewer IS the seller — show their own AuthContext avatar
        if (authProfileImage) { setSellerProfileImageUrl(authProfileImage); return; }
        return; // authProfileImage not yet hydrated — effect re-runs when it does
      }

      // Step 2: Viewing someone else's listing — use product-embedded fields
      const productCandidates = [
        resolve(product?.seller_profile_image_url),
        resolve(product?.seller_profile_image),
        resolve(product?.seller_avatar_url),
        resolve(product?.seller_avatar),
      ].filter(Boolean);

      for (const candidate of productCandidates) {
        if (candidate.includes('default') || candidate.includes('placeholder')) continue;
        const ok = await probeImage(candidate);
        if (ok) { setSellerProfileImageUrl(candidate); return; }
      }

      // Step 3: Auth-token fetch intentionally removed.
      // Fetching /auth/profile/ with the logged-in token returns the CURRENT
      // user's photo, not the seller's — causing the wrong avatar to appear
      // on ads posted by other users. Skip straight to the public API.

      // Step 4: Public seller-profile API (other sellers' listings)
      if (!sellerIdentifier) return;
      try {
        const data = await apiService.getSellerProfile?.(sellerIdentifier);
        if (data) {
          const img = resolve(data?.profile_image_url || data?.profile_image);
          if (img) setSellerProfileImageUrl(img);
          setSellerProfile(data || null);
        }
      } catch (_) { /* fall back to initials avatar */ }
    };

    resolveSellerImage();
  }, [sellerIdentifier, product, authProfileImage]);

  // ── Derived values ────────────────────────────────────────────────────────
  const isNegotiable    = product.price_type === 'negotiable';
  const conditionKey    = product.condition || '';
  const conditionConfig = CONDITION_CONFIG[conditionKey];
  const conditionLabel  = product.condition_display || conditionConfig?.label || null;
  const conditionEmoji  = conditionConfig?.emoji || '📋';
  const category = product.category_name ||
    (typeof product.category === 'object' ? product.category?.name : product.category) || null;
  const description = product.description || '';

  // ── Seller display name & initials ───────────────────────────────────────
  // Mask email fallback: strip trailing digits + domain so "kiberober65@gmail.com" → "kiberober"
  const maskEmail = (v) => {
    if (!v || !v.includes('@')) return v;
    return v.split('@')[0].replace(/\d+$/, '');
  };
  const sellerName = product.seller_name || product.seller_username || maskEmail(sellerIdentifier) || 'Seller';
  // Display name: masks email in all fallback sources
  const sellerDisplayName = maskEmail(product.seller_name) || maskEmail(product.seller_username) || maskEmail(sellerIdentifier) || 'Seller';
  const sellerInitials = sellerName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(w => w[0].toUpperCase())
    .join('');

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className={styles.wrap}>

      {/* ── Name ── */}
      <h2 className={styles.name}>{product.name || product.title}</h2>

      {/* ── Price + badge ── */}
      <div className={styles.priceRow}>
        <span className={styles.price}>
          KES {Number(product.price).toLocaleString()}
        </span>
        <span className={`${styles.badge} ${isNegotiable ? styles.badgeNeg : styles.badgeFixed}`}>
          {isNegotiable ? '💬 Negotiable' : '🔒 Fixed'}
        </span>
      </div>

      {/* ── Meta grid ── */}
      <div className={styles.meta}>
        {(product.city || product.location) && (
          <div className={styles.metaItem}>
            <span className={styles.metaIcon}>📍</span>
            <span className={styles.metaVal}>{product.city || product.location}</span>
          </div>
        )}
        {conditionLabel && (
          <div className={styles.metaItem}>
            <span className={styles.metaIcon}>{conditionEmoji}</span>
            <span className={styles.metaVal}>{conditionLabel}</span>
          </div>
        )}
        {category && (
          <div className={styles.metaItem}>
            <span className={styles.metaIcon}>🏷️</span>
            <span className={styles.metaVal}>{category}</span>
          </div>
        )}
      </div>

      {/* ── Seller mini-profile ── */}
      {sellerIdentifier && (
        <div className={styles.sellerMini} style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '10px 12px', borderRadius: 10,
          background: '#f8fafc', border: '1px solid #e5e7eb',
          marginBottom: 12, overflow: 'hidden', minWidth: 0,
          boxSizing: 'border-box', width: '100%',
        }}>
          {/* ✅ Seller profile image — or initials/SVG fallback */}
          {sellerProfileImageUrl ? (
            <img
              src={sellerProfileImageUrl}
              alt={sellerName}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                e.currentTarget.nextSibling.style.display = 'flex';
              }}
              style={{
                width: 38, height: 38, borderRadius: '50%',
                objectFit: 'cover', border: '2px solid #e5e7eb', flexShrink: 0,
              }}
            />
          ) : null}
          {/* SVG user-icon fallback — shown when no image or image fails */}
          <div style={{
            width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
            background: 'linear-gradient(135deg,#6366f1,#8b5cf6)',
            display: sellerProfileImageUrl ? 'none' : 'flex',
            alignItems: 'center', justifyContent: 'center',
            border: '2px solid #e5e7eb', color: '#fff',
          }}>
            {sellerInitials ? (
              <span style={{ fontWeight: 700, fontSize: 14 }}>{sellerInitials}</span>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                <circle cx="12" cy="7" r="4"/>
              </svg>
            )}
          </div>

          <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#111', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {sellerDisplayName}
            </div>
            {/* ✅ FIX 3: followCount from useFollowSeller via props — in sync with SellerSidebar */}
            {(followCount ?? 0) > 0 && (
              <div style={{ fontSize: 11, color: '#6b7280' }}>
                {followCount} follower{followCount !== 1 ? 's' : ''}
              </div>
            )}
          </div>

          {/* ✅ FIX 3: Follow button wired to parent-provided handler + state */}
          <button
            onClick={onFollowToggle}
            disabled={isFollowLoading}
            style={{
              padding: '5px 10px', borderRadius: 20, fontSize: 12, fontWeight: 700,
              cursor: isFollowLoading ? 'not-allowed' : 'pointer',
              border: '1.5px solid #6366f1',
              background: isFollowing ? '#ede9fe' : '#6366f1',
              color: isFollowing ? '#4338ca' : '#fff',
              transition: 'all 0.15s', flexShrink: 0, whiteSpace: 'nowrap',
              opacity: isFollowLoading ? 0.65 : 1,
            }}
          >
            {isFollowLoading ? (
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <svg
                  width="11" height="11" viewBox="0 0 24 24"
                  fill="none" stroke="currentColor" strokeWidth="2.5"
                  style={{ animation: 'pis-spin 0.75s linear infinite' }}
                >
                  <circle cx="12" cy="12" r="10" strokeOpacity="0.2" />
                  <path d="M12 2a10 10 0 0 1 10 10" />
                </svg>
                …
              </span>
            ) : isFollowing ? '✓ Following' : '+ Follow'}
          </button>
        </div>
      )}

      {/* ── Description — scrollable capped box ── */}
      {description && (
        <div className={styles.desc}>
          <div className={styles.descTitle}>Product Description</div>
          <div
            ref={descRef}
            className={`${styles.descBody} ${descExpanded ? styles.descBodyExpanded : ''}`}
          >
            <p className={styles.descText}>{description}</p>
          </div>

          {!descExpanded && descOverflows && (
            <div className={styles.descFade} />
          )}

          {descOverflows && (
            <button className={styles.descToggle} onClick={() => setDescExpanded(v => !v)}>
              {descExpanded ? 'Show less ▲' : 'Read more ▼'}
            </button>
          )}
        </div>
      )}

      {/* ── Copy link ── */}
      <button className={styles.copyBtn} onClick={onCopyLink}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
        Copy Link
      </button>

      {showCopyNotification && (
        <div className={styles.copyNote}>✅ Link copied — share it!</div>
      )}

      {/* ── Primary actions ── */}
      <div className={styles.actions}>
        <button className={styles.btnPrimary} onClick={onContactSeller}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
              stroke="currentColor" strokeWidth="2"/>
            <path d="m22 6-10 7L2 6" stroke="currentColor" strokeWidth="2"/>
          </svg>
          Contact Seller
        </button>
        {isNegotiable && (
          <button className={styles.btnSecondary} onClick={onMakeOffer}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <rect x="2" y="7" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="2"/>
              <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" stroke="currentColor" strokeWidth="2"/>
            </svg>
            Make Offer
          </button>
        )}
      </div>

      {/* ── Secondary actions ── */}
      <button className={styles.btnGreen} onClick={onMessageSeller}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
            stroke="currentColor" strokeWidth="2"/>
        </svg>
        Message Seller
      </button>

      <div className={styles.tertiaryActions}>
        <button className={styles.btnOutlineAmber} onClick={onOpenRating}>⭐ Rate Seller</button>
        <button className={styles.btnOutlineGray}  onClick={onSaveToWishlist}>🔖 Save</button>
      </div>

      {/* ── Revealed contact ── */}
      {showSellerInfo && checkAuthentication().isAuthenticated && (
        <div className={styles.contactReveal}>
          <strong>📧 Contact</strong>
          {product.seller_name && <p>{product.seller_name}</p>}
          {(product.phone || product.contact_number_1 || product.contact) && (
            <p>{product.phone || product.contact_number_1 || product.contact}</p>
          )}
        </div>
      )}

      {/* ── Physical Business Address — map + photos from seller profile ── */}
      {/* Pass product directly so the card renders immediately from ad data  */}
      {/* even before the async sellerProfile fetch completes.                */}
      {/* <PhysicalBusinessAddress
        sellerProfile={sellerProfile}
        sellerEmail={sellerIdentifier}
        product={product}
      /> */}

      <style>{`
        @keyframes pis-spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ProductInfoSection;
